import React from 'react'
import type { InlineInputContentOptions, InlineInputEmptyContent } from '@ztechapps/webui.input'
import { EmptyMembers, MembersInlineInput } from '@ztechapps/webui.input'
import type { Task, User } from '@ztechapps/common.contract'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { AvatarGroup, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { type InlineEditorOnClick } from '@ztechapps/common.hook'
import get from 'lodash/get'
import { useAssignTaskToMe } from '@ztechapps/task.common'
import map from 'lodash/map'
import type { SetOptional } from 'type-fest'
import type { TaskInlineInputProps } from './TaskInlineInput'
import { TaskInlineInput } from './TaskInlineInput'

export const TaskMembersEmptyContent: InlineInputEmptyContent = (
  onClick: InlineEditorOnClick,
  { documentId, canEdit }: InlineInputContentOptions
) => {
  const [assignTaskToMe, { loading }] = useAssignTaskToMe()

  const handleAssignToMe = async () => {
    await assignTaskToMe({ _id: documentId } as Task)
  }

  return (
    <HorizontalStack gap={1}>
      <div onClick={onClick}>
        <EmptyMembers />
      </div>
      <ShowIf condition={canEdit}>
        <BusyButtonLink
          disabled={!canEdit}
          bsSize="sm"
          text="Assign to me"
          icon="user-check"
          busyText="Assigning..."
          isBusy={loading}
          onClick={handleAssignToMe}
        />
      </ShowIf>
    </HorizontalStack>
  )
}
export type TaskMembersInlineInputProps = SetOptional<TaskInlineInputProps, 'name'>

export const TaskMembersInlineInput = ({
  canEdit,
  emptyContent,
  displayContent,
  parentInputComponent,
  initialValues,
  label = 'Assigned Users',
  name = 'members',
  help = 'The members for this task',
  ...rest
}: TaskMembersInlineInputProps) => {
  const ParentInput = parentInputComponent || TaskInlineInput

  const queryName = `members {
    _id
    profile {
      _id
      name
      email
      picture
    }
  }`

  const members: User[] = get(initialValues, 'members', [])
  const memberId = members.map((m) => m._id)

  //TOOD: should include the value and label to work with dropdown
  const finalInitialValues = {
    ...initialValues,
  }

  const filter = {
    _id: { $nin: memberId },
  }

  emptyContent = emptyContent ?? TaskMembersEmptyContent
  displayContent =
    displayContent ??
    ((initialValue: any, onClick: any) => {
      return (
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault()
            return onClick()
          }}
        >
          <MemberAvatar users={initialValue} />
        </a>
      )
    })

  return (
    <ParentInput
      {...{
        canEdit,
        label,
        help,
        name,
        queryName,
        ...rest,
        inputProps: { filter },
        parentInputComponent: MembersInlineInput,
      }}
      initialValues={finalInitialValues}
      hoverToEdit={false}
      emptyContent={emptyContent}
      displayContent={displayContent}
    />
  )
}

const MemberAvatar = ({ users }: { users: User[] }) => {
  const dataArray = map(users, (e) => {
    let picture = e?.profile.picture ?? ''
    if (picture.includes('/dft-customer.jpg')) {
      picture = null
    }

    return { ...e.profile, src: picture }
  })

  return <AvatarGroup dataArray={dataArray} size={30} />
}
